import type { ApiResponse } from "apisauce"

import { request } from "../../../../../services/api"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import {
  type DirectionName,
  type FinancialActionName,
  type ProjectOption,
  type Receipt,
  type ReceiptDetails
} from "./services.types.ts"

const BASE_URL = "/receipts"

interface ErrorDetail {
  detail?: string
}

// The backend envelopes every successful POST/PUT/PATCH/DELETE response as
// `{ status, details: { data } }` (EnvelopeJSONRenderer, ENVELOPE_METHODS);
// GET responses are bare. `unwrapEnvelope` is a no-op on an already-bare
// payload, so it is safe to apply uniformly here rather than have every
// caller know which shape its own verb produces. A failure is not enveloped
// either — a 400 like `{"detail": "..."}` is read for its real message
// before falling back to the generic one.
async function unwrap<T>(
  requestPromise: Promise<ApiResponse<T, ErrorDetail>>,
  errorMessage: string
): Promise<T> {
  const response = await requestPromise
  if (response.ok && response.data) {
    return unwrapEnvelope<T>(response.data)
  }
  const detail = !response.ok ? response.data?.detail : undefined
  throw new Error(detail || errorMessage)
}

export function uploadReceipt({
  file,
  projectId
}: {
  file: File
  projectId?: string
}): Promise<Receipt> {
  const form = new FormData()
  form.append("file", file)
  if (projectId) form.append("project_id", projectId)
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(`${BASE_URL}/`, form, {
      headers: { "Content-Type": "multipart/form-data" }
    }),
    "Upload failed"
  )
}

export function getReceipt(id: string): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.get<Receipt, ErrorDetail>(`${BASE_URL}/${id}/`),
    "Fetch failed"
  )
}

export function listReceipts(
  params?: Record<string, string>
): Promise<{ results: Receipt[]; count: number }> {
  return unwrap<{ results: Receipt[]; count: number }>(
    request.api.get<{ results: Receipt[]; count: number }, ErrorDetail>(
      `${BASE_URL}/`,
      params
    ),
    "List failed"
  )
}

export function resolveMatch(id: string, projectId: string): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(`${BASE_URL}/${id}/project/`, {
      project_id: projectId
    }),
    "Resolve failed"
  )
}

export function updateReceiptDetails(
  id: string,
  details: ReceiptDetails
): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(
      `${BASE_URL}/${id}/details/`,
      details
    ),
    "Save failed"
  )
}

export function getProjectOptions(
  id: string,
  q: string
): Promise<ProjectOption[]> {
  return unwrap<ProjectOption[]>(
    request.api.get<ProjectOption[], ErrorDetail>(
      `${BASE_URL}/${id}/project-options/`,
      q ? { q } : {}
    ),
    "Projects failed"
  )
}

export function getActionRequirements(
  id: string,
  action: FinancialActionName
): Promise<{ missing: string[] }> {
  return unwrap<{ missing: string[] }>(
    request.api.get<{ missing: string[] }, ErrorDetail>(
      `${BASE_URL}/${id}/action-requirements/`,
      { action }
    ),
    "Requirements failed"
  )
}

export function chooseAction(
  id: string,
  action: FinancialActionName,
  answers: { reimbursement_person?: string; contractor?: string },
  direction: DirectionName
): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(`${BASE_URL}/${id}/action/`, {
      action,
      direction,
      ...answers
    }),
    "Action failed"
  )
}

export function createFinancialLine(
  id: string,
  projectTypeId?: string
): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(
      `${BASE_URL}/${id}/create-financial-line/`,
      {
        project_type_id: projectTypeId
      }
    ),
    "Create failed"
  )
}

export function retryReceipt(id: string): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(`${BASE_URL}/${id}/retry/`),
    "Retry failed"
  )
}

export function dismissReceipt(id: string, reason: string): Promise<Receipt> {
  return unwrap<Receipt>(
    request.api.post<Receipt, ErrorDetail>(`${BASE_URL}/${id}/dismiss/`, {
      reason
    }),
    "Dismiss failed"
  )
}
